import { memo, useId } from 'react';
import type { Chord } from '@/types/music';
import './ChordDiagram.css';

interface ChordDiagramProps {
  chord: Chord;
  /** Diagram width in px. Everything else scales from it. */
  size?: number;
  /** Show finger numbers inside the dots. */
  showFingers?: boolean;
  /** Number of fret rows drawn. */
  frets?: number;
  className?: string;
}

/**
 * The chord diagram, rendered from structured data — never from an image.
 *
 * Orientation follows every guitar chart the player will ever see: the nut is
 * the thick line at the top, strings run vertically with the 6th (low E) on the
 * left, and frets run across. `×` above a string means do not play it, `○`
 * means play it open.
 *
 * This component is the product's signature object, so it is drawn as a real
 * thing: a heavy nut, hairline strings, and dots with genuine weight — not a
 * generic grid of squares.
 */
function ChordDiagramImpl({
  chord,
  size = 200,
  showFingers = true,
  frets = 4,
  className,
}: ChordDiagramProps) {
  const titleId = useId();

  const paddingX = size * 0.09;
  const markerRow = size * 0.13;
  const nutY = markerRow + size * 0.05;
  const gridWidth = size - paddingX * 2;
  const stringGap = gridWidth / 5;
  const fretHeight = size * 0.158;
  const gridHeight = fretHeight * frets;
  const height = nutY + gridHeight + size * 0.06;
  // Kept comfortably inside half a fret space so a first-fret dot never
  // collides with the nut, which is drawn thick.
  const dotRadius = Math.min(stringGap * 0.34, fretHeight * 0.38);

  // Chord charts start at fret 1 unless the shape sits higher up the neck.
  const baseFret = chord.baseFret;
  const stringX = (index: number) => paddingX + index * stringGap;
  const fretY = (fret: number) => nutY + (fret - baseFret + 0.5) * fretHeight;

  return (
    <svg
      className={['chord-diagram', className].filter(Boolean).join(' ')}
      viewBox={`0 0 ${size} ${height}`}
      width={size}
      height={height}
      role="img"
      aria-labelledby={titleId}
    >
      <title id={titleId}>{describeChord(chord)}</title>

      {/* Открытые и заглушенные струны */}
      {chord.frets.map((fret, index) => {
        const x = stringX(index);
        const y = markerRow;
        if (fret === null) {
          const arm = size * 0.026;
          return (
            <g key={`marker-${index}`} className="chord-diagram__muted">
              <line x1={x - arm} y1={y - arm} x2={x + arm} y2={y + arm} />
              <line x1={x - arm} y1={y + arm} x2={x + arm} y2={y - arm} />
            </g>
          );
        }
        if (fret === 0) {
          return (
            <circle
              key={`marker-${index}`}
              className="chord-diagram__open"
              cx={x}
              cy={y}
              r={size * 0.028}
            />
          );
        }
        return null;
      })}

      {/* Порожек: толстая линия только когда аккорд в первой позиции */}
      <line
        className={baseFret === 1 ? 'chord-diagram__nut' : 'chord-diagram__fret'}
        x1={paddingX}
        y1={nutY}
        x2={size - paddingX}
        y2={nutY}
      />

      {Array.from({ length: frets }, (_, i) => (
        <line
          key={`fret-${i}`}
          className="chord-diagram__fret"
          x1={paddingX}
          y1={nutY + (i + 1) * fretHeight}
          x2={size - paddingX}
          y2={nutY + (i + 1) * fretHeight}
        />
      ))}

      {chord.frets.map((_, index) => (
        <line
          key={`string-${index}`}
          className="chord-diagram__string"
          x1={stringX(index)}
          y1={nutY}
          x2={stringX(index)}
          y2={nutY + gridHeight}
        />
      ))}

      {baseFret > 1 && (
        <text className="chord-diagram__position" x={paddingX - size * 0.045} y={fretY(baseFret)}>
          {baseFret}
        </text>
      )}

      {chord.barre && (
        <rect
          className="chord-diagram__barre"
          x={stringX(6 - chord.barre.toString) - dotRadius}
          y={fretY(chord.barre.fret) - dotRadius}
          width={(chord.barre.toString - chord.barre.fromString) * stringGap + dotRadius * 2}
          height={dotRadius * 2}
          rx={dotRadius}
        />
      )}

      {chord.frets.map((fret, index) => {
        if (fret === null || fret === 0) return null;
        const x = stringX(index);
        const y = fretY(fret);
        const finger = chord.fingers[index];
        return (
          <g key={`dot-${index}`}>
            <circle className="chord-diagram__dot" cx={x} cy={y} r={dotRadius} />
            {showFingers && finger > 0 && (
              <text className="chord-diagram__finger" x={x} y={y} fontSize={dotRadius * 1.15}>
                {finger}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Memoised: it is a pure function of its props, and the exercise screen updates
 * its snapshot about twelve times a second while the diagram never changes.
 */
export const ChordDiagram = memo(ChordDiagramImpl);

/** Accessible description — a screen reader gets the fingering, not "image". */
function describeChord(chord: Chord): string {
  const parts = chord.frets.map((fret, index) => {
    const stringNumber = 6 - index;
    if (fret === null) return `${stringNumber}-я не играется`;
    if (fret === 0) return `${stringNumber}-я открытая`;
    return `${stringNumber}-я на ${fret} ладу`;
  });
  return `Аккорд ${chord.name}. ${parts.join(', ')}.`;
}
